import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

/**
 * Transient messages, in one place.
 *
 * Before this the app said things three different ways: an inline red
 * paragraph that scrolled off the top of a long form, a `window.alert` that
 * blocks the tab and looks like a browser failure rather than the product
 * speaking, and — most often — nothing at all, because wiring a fourth error
 * line into a screen was more work than swallowing the error.
 *
 * Toasts are for what happened, not for what is wrong with the thing you are
 * editing. A field that will not validate belongs beside the field, where it
 * can be read while fixing it; "removed", "could not remove", "saved" belong
 * here, because they are about an act that is already over.
 */

export type ToastTone = 'info' | 'success' | 'warning' | 'error'

export interface Toast {
  readonly id: number
  readonly tone: ToastTone
  readonly message: string
  /** A second line for the reason, when the headline alone cannot carry it. */
  readonly detail?: string | undefined
  /**
   * The one thing to do about it. A toast that asks for something stays
   * until it is done or dismissed, whatever its tone.
   */
  readonly action?: ToastAction | undefined
}

export interface ToastAction {
  readonly label: string
  readonly onSelect: () => void
}

interface ToastApi {
  show(
    message: string,
    options?: {
      tone?: ToastTone
      detail?: string | undefined
      action?: ToastAction | undefined
    },
  ): void
  dismiss(id: number): void
}

const ToastContext = createContext<ToastApi | undefined>(undefined)

/**
 * How long a toast stays.
 *
 * Errors do not leave on their own. A message that says something failed is
 * the one a person needs to still be there when they look back at the screen,
 * and a four-second error is indistinguishable from no error at all.
 */
export const LINGER: Record<ToastTone, number> = {
  info: 4000,
  success: 4000,
  warning: 8000,
  error: Number.POSITIVE_INFINITY,
}

/** How long this one stays: its tone's time, or for good when it asks for something. */
export function lingerOf(toast: Pick<Toast, 'tone' | 'action'>): number {
  return toast.action === undefined ? LINGER[toast.tone] : Number.POSITIVE_INFINITY
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<readonly Toast[]>([])

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((t) => t.id !== id))
  }, [])

  const show = useCallback<ToastApi['show']>((message, options) => {
    const tone = options?.tone ?? 'info'
    // Counted from the last one rather than from a clock: two toasts raised in
    // the same millisecond would share a timestamp, and React would treat them
    // as one row.
    setToasts((current) => [
      ...current,
      {
        id: (current[current.length - 1]?.id ?? 0) + 1,
        tone,
        message,
        detail: options?.detail,
        action: options?.action,
      },
    ])
  }, [])

  const api = useMemo(() => ({ show, dismiss }), [show, dismiss])

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastRail toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  )
}

/**
 * Toasting from anywhere, without every screen threading a prop.
 *
 * Outside a provider this is a no-op rather than a crash: a ingredient rendered
 * in isolation — a test, a story — should not fail because nothing was there
 * to receive a message it tried to send.
 */
export function useToast(): ToastApi {
  const api = useContext(ToastContext)
  return (
    api ?? {
      show: () => undefined,
      dismiss: () => undefined,
    }
  )
}

function ToastRail({
  toasts,
  onDismiss,
}: {
  toasts: readonly Toast[]
  onDismiss: (id: number) => void
}) {
  if (toasts.length === 0) return null

  return (
    /*
     * Announced politely rather than assertively: these arrive after an act
     * the person just performed, so a screen reader interrupting mid-sentence
     * would be louder than the news.
     */
    <div
      className="pointer-events-none fixed inset-x-0 top-0 z-[60] flex flex-col items-center gap-2 p-4"
      role="status"
      aria-live="polite"
    >
      {toasts.map((toast) => (
        <ToastCard key={toast.id} toast={toast} onDismiss={() => onDismiss(toast.id)} />
      ))}
    </div>
  )
}

const TONE: Record<ToastTone, string> = {
  info: 'border-stroke-weak bg-background-raised text-text-strong',
  success: 'border-stroke-brand-strong bg-fill-brand-weak text-text-strong',
  warning: 'border-stroke-weak bg-fill-warning-weak text-text-strong',
  error: 'border-stroke-error-strong bg-fill-error-weak text-text-strong',
}

function ToastCard({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  useEffect(() => {
    const linger = lingerOf(toast)
    if (!Number.isFinite(linger)) return undefined
    const timer = setTimeout(onDismiss, linger)
    return () => clearTimeout(timer)
  }, [toast, onDismiss])

  return (
    <div
      className={`rounded-base pointer-events-auto flex w-full max-w-[32rem] items-start gap-3 border px-4 py-3 shadow-lg ${TONE[toast.tone]}`}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-tP">{toast.message}</span>
        {toast.detail !== undefined && (
          <span className="text-tSmall text-text-weak break-words">{toast.detail}</span>
        )}
        {toast.action !== undefined && (
          <button
            type="button"
            onClick={() => {
              toast.action?.onSelect()
              onDismiss()
            }}
            className="text-tP text-text-brand self-start underline"
          >
            {toast.action.label}
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Fechar aviso"
        className="text-text-weak hover:text-text-strong text-tP shrink-0 px-1"
      >
        ✕
      </button>
    </div>
  )
}
